// Documento de transporte (DDT) del proveedor: UN PDF por contenedor
// (2026-10-23, petición del cliente). Lo sube el proveedor —o el admin— desde
// la ficha del contenedor y el portal lo ve y lo descarga.
//
// Sin conexión, la petición se guarda en su propia cola (deliveryNoteQueue) y
// sale sola al volver la red. Es una cola APARTE de la de detalles y fotos a
// propósito: no toca su lógica, y como subir un DDT sustituye al anterior en
// el servidor, basta con guardar la última petición por contenedor y
// repetirla las veces que haga falta.
import axios from 'axios';
import { uploadDeliveryNote, deleteDeliveryNote } from './api';
import {
  getQueuedDeliveryNotes,
  getQueuedDeliveryNote,
  putQueuedDeliveryNote,
  deleteQueuedDeliveryNote,
  type QueuedDeliveryNote,
} from './db';
import type { DeliveryNote } from '../types';

/** El mismo tope que el servidor. Un DDT escaneado de una página ronda 0,5 MB. */
export const MAX_DELIVERY_NOTE_BYTES = 10 * 1024 * 1024;

/** Se emite cada vez que cambia la cola del DDT, para que las pantallas se refresquen. */
export const DELIVERY_NOTES_CHANGED_EVENT = 'deliverynotes:changed';

const RETRY_BASE_MS = 5_000;
const RETRY_MAX_MS = 5 * 60_000;

export type PdfCheck = 'ok' | 'empty' | 'tooBig' | 'notPdf';

/**
 * ¿Es un PDF que el servidor va a aceptar? Se mira el contenido ("%PDF-"), no
 * la extensión ni el tipo que dice el navegador: una foto renombrada a .pdf
 * no es un DDT.
 */
export async function checkPdf(file: Blob): Promise<PdfCheck> {
  if (!file || file.size === 0) return 'empty';
  if (file.size > MAX_DELIVERY_NOTE_BYTES) return 'tooBig';
  const head = new Uint8Array(await readBytes(file.slice(0, 5)));
  const isPdf = head.length === 5 && head[0] === 0x25 && head[1] === 0x50 && head[2] === 0x44 && head[3] === 0x46 && head[4] === 0x2d;
  return isPdf ? 'ok' : 'notPdf';
}

export type DeliveryNoteOutcome =
  | { kind: 'done'; note?: DeliveryNote }
  | { kind: 'queued' }
  | { kind: 'rejected'; message: string };

/** Sube el PDF como DDT del contenedor. Sin red (o con el servidor caído), lo deja en la cola. */
export async function saveDeliveryNote(
  idCargoDetail: number,
  idCargo: number,
  file: Blob,
  fileName: string,
): Promise<DeliveryNoteOutcome> {
  try {
    const response = await uploadDeliveryNote(idCargoDetail, file, fileName);
    await forgetQueued(idCargoDetail);
    return { kind: 'done', note: response.data };
  } catch (err) {
    if (!isRetryable(err)) return { kind: 'rejected', message: rejectionMessage(err) };
    await putQueuedDeliveryNote({
      idCargoDetail,
      idCargo,
      op: 'upload',
      dataUrl: await toDataUrl(file),
      fileName,
      queuedAt: Date.now(),
      attempts: 0,
    });
    notifyChanged();
    return { kind: 'queued' };
  }
}

/** Quita el DDT del contenedor. Sin red, lo deja en la cola. */
export async function removeDeliveryNote(idCargoDetail: number, idCargo: number): Promise<DeliveryNoteOutcome> {
  try {
    await deleteDeliveryNote(idCargoDetail);
    await forgetQueued(idCargoDetail);
    return { kind: 'done' };
  } catch (err) {
    if (!isRetryable(err)) return { kind: 'rejected', message: rejectionMessage(err) };
    await putQueuedDeliveryNote({ idCargoDetail, idCargo, op: 'delete', queuedAt: Date.now(), attempts: 0 });
    notifyChanged();
    return { kind: 'queued' };
  }
}

/** Lo que queda pendiente para un contenedor, o null. */
export function getPendingDeliveryNote(idCargoDetail: number): Promise<QueuedDeliveryNote | null> {
  return getQueuedDeliveryNote(idCargoDetail);
}

/** Descarta lo pendiente de un contenedor (p. ej. un PDF que el servidor rechazó). */
export async function discardPendingDeliveryNote(idCargoDetail: number): Promise<void> {
  await forgetQueued(idCargoDetail);
}

let flushing: Promise<void> | null = null;

/**
 * Manda lo pendiente. Una sola pasada a la vez; lo que falla por red espera
 * cada vez más (5 s, 10 s… hasta 5 min) y lo que el servidor rechaza se queda
 * marcado con su motivo, sin reintentarse.
 */
export function flushDeliveryNotes(now: () => number = Date.now): Promise<void> {
  if (!flushing) {
    flushing = runFlush(now).finally(() => { flushing = null; });
  }
  return flushing;
}

async function runFlush(now: () => number): Promise<void> {
  let items: QueuedDeliveryNote[];
  try {
    items = await getQueuedDeliveryNotes();
  } catch {
    return;
  }

  for (const item of items.sort((a, b) => a.queuedAt - b.queuedAt)) {
    if (item.error || (item.nextAttemptAt ?? 0) > now()) continue;
    try {
      if (item.op === 'upload') {
        await uploadDeliveryNote(item.idCargoDetail, fromDataUrl(item.dataUrl ?? ''), item.fileName || 'ddt.pdf');
      } else {
        await deleteDeliveryNote(item.idCargoDetail);
      }
      await forgetIfUnchanged(item);
    } catch (err) {
      const attempts = item.attempts + 1;
      await saveIfUnchanged(item, isRetryable(err)
        ? { ...item, attempts, nextAttemptAt: now() + Math.min(RETRY_BASE_MS * 2 ** Math.min(attempts, 10), RETRY_MAX_MS) }
        : { ...item, attempts, error: rejectionMessage(err) });
    }
  }
}

// Mientras se mandaba, el proveedor pudo pedir otra cosa para ese contenedor
// (otro PDF, quitarlo): esa petición nueva manda y no se pisa.
async function forgetIfUnchanged(item: QueuedDeliveryNote): Promise<void> {
  const current = await getQueuedDeliveryNote(item.idCargoDetail);
  if (current && current.queuedAt === item.queuedAt) await forgetQueued(item.idCargoDetail);
}

async function saveIfUnchanged(item: QueuedDeliveryNote, next: QueuedDeliveryNote): Promise<void> {
  const current = await getQueuedDeliveryNote(item.idCargoDetail);
  if (current && current.queuedAt === item.queuedAt) {
    await putQueuedDeliveryNote(next);
    notifyChanged();
  }
}

async function forgetQueued(idCargoDetail: number): Promise<void> {
  try {
    await deleteQueuedDeliveryNote(idCargoDetail);
  } catch {
    // Sin IndexedDB no había nada encolado que olvidar.
  }
  notifyChanged();
}

function notifyChanged(): void {
  try {
    window.dispatchEvent(new Event(DELIVERY_NOTES_CHANGED_EVENT));
  } catch { /* fuera del navegador */ }
}

// Sin respuesta (red caída, tiempo agotado, "Trabajar offline") o con el
// servidor fallando, se reintenta. Un 4xx es un "no" del servidor: reintentar
// no lo cambia.
function isRetryable(err: unknown): boolean {
  if (!axios.isAxiosError(err)) return true;
  const status = err.response?.status;
  if (!status) return true;
  return status >= 500 || status === 408 || status === 429;
}

function rejectionMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data;
    if (typeof data === 'string' && data.trim()) return data.trim().slice(0, 200);
    if (err.response?.status) return `HTTP ${err.response.status}`;
  }
  return err instanceof Error ? err.message : String(err);
}

function readBytes(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

async function toDataUrl(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await readBytes(blob));
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return `data:application/pdf;base64,${btoa(binary)}`;
}

function fromDataUrl(dataUrl: string): Blob {
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: 'application/pdf' });
}
